import { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation } from "react-router";
import {
  BarChart3,
  ChevronRight,
  FileQuestion,
  FileSearch,
  LayoutDashboard,
  LogOut,
  PanelLeftClose,
  PanelLeftOpen,
  Users,
  Zap,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useAuth0 } from "@auth0/auth0-react";
import {
  StudioFeatures,
  type StudioFeatureDefinition,
  type StudioFeatureKey,
  type StudioFeatureNavGroup,
} from "@intelliextract-studio/shared";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";
import { useFeatures } from "@/hooks/useFeatures";
import { sessionRepository } from "@/infrastructure/repositories/session/Session.repository";
import { sidebarStateRepository } from "@/infrastructure/repositories/storage/SidebarState.repository";
import { Button } from "@/presentation/components/ui";
import ThemeToggle from "@/presentation/components/layout/ThemeToggle";

const FEATURE_ICONS: Record<StudioFeatureKey, LucideIcon> = {
  dashboard: LayoutDashboard,
  requests: FileSearch,
  usage: BarChart3,
  STRANGER_FILES: FileQuestion,
  consumers: Users,
  patterns: FileSearch,
  review: FileSearch,
  dlq: FileSearch,
  rules: FileSearch,
  settings: FileSearch,
};

function buildNavGroups(
  accessibleFeatures: StudioFeatureDefinition[],
): Array<{ group: StudioFeatureNavGroup; items: StudioFeatureDefinition[] }> {
  return StudioFeatures.getNavGroups()
    .map((group) => ({
      group,
      items: accessibleFeatures.filter((feature) => feature.navGroup === group),
    }))
    .filter((entry) => entry.items.length > 0);
}

function initials(name?: string | null, email?: string | null): string {
  if (name) {
    return name
      .split(" ")
      .map((part) => part[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  }
  return (email?.[0] ?? "U").toUpperCase();
}

function navIconWrapClass(collapsed: boolean, active: boolean): string {
  if (!collapsed) {
    return "flex shrink-0 items-center justify-center";
  }
  if (active) {
    return "flex shrink-0 items-center justify-center rounded-md bg-sidebar-primary/20 p-1.5 text-sidebar-primary";
  }
  return "flex shrink-0 items-center justify-center p-1.5";
}

function UserAvatar({
  userPicture,
  pictureFailed,
  onPictureError,
  userName,
  userEmail,
  size = "md",
}: Readonly<{
  userPicture?: string;
  pictureFailed: boolean;
  onPictureError: () => void;
  userName?: string | null;
  userEmail?: string | null;
  size?: "md" | "lg";
}>) {
  const sizeClass = size === "lg" ? "h-8 w-8 text-xs" : "h-7 w-7 text-[10px]";

  return (
    <div
      className={cn(
        "sidebar-user-avatar flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#216869] font-semibold leading-none text-white transition-opacity",
        sizeClass,
      )}
    >
      {userPicture && !pictureFailed ? (
        <img
          src={userPicture}
          alt=""
          className="h-full w-full rounded-full object-cover"
          onError={onPictureError}
        />
      ) : (
        initials(userName, userEmail)
      )}
    </div>
  );
}

const Sidebar = () => {
  const location = useLocation();
  const { logout } = useAuth();
  const { user } = useAuth0();
  const { accessibleFeatures } = useFeatures();
  const sessionUser = sessionRepository.getUserInfo();
  const [collapsed, setCollapsed] = useState(false);

  const navGroups = useMemo(() => buildNavGroups(accessibleFeatures), [accessibleFeatures]);

  const userName =
    user?.name ??
    ([sessionUser?.firstName, sessionUser?.lastName].filter(Boolean).join(" ") || null);
  const userEmail = user?.email ?? sessionUser?.email ?? null;
  const userPicture = user?.picture ?? undefined;
  const userRoles = sessionRepository.getUserRoles();
  const userRoleLabel = userRoles.length > 0 ? userRoles?.join(", ") : null;
  const [pictureFailed, setPictureFailed] = useState(false);

  useEffect(() => {
    void sidebarStateRepository.getCollapsed().then(setCollapsed);
  }, []);

  useEffect(() => {
    setPictureFailed(false);
  }, [userPicture]);

  function toggle() {
    setCollapsed((prev) => {
      const next = !prev;
      void sidebarStateRepository.setCollapsed(next);
      return next;
    });
  }

  function isActive(path: string) {
    if (path === "/dashboard") return location.pathname === "/dashboard";
    return location.pathname.startsWith(path);
  }

  return (
    <div
      className={cn(
        "studio-sidebar text-sidebar-foreground transition-all duration-200 ease-in-out",
        collapsed ? "w-16" : "w-60",
      )}
    >
      <div
        className={cn(
          "flex h-14 shrink-0 items-center border-b border-sidebar-border transition-all duration-200",
          collapsed ? "justify-center px-0" : "gap-2.5 px-4",
        )}
      >
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#216869]">
          <Zap className="h-4 w-4 text-white" />
        </div>
        {!collapsed && (
          <div className="flex min-w-0 flex-1 flex-col justify-center leading-none">
            <span className="truncate text-sm font-semibold leading-tight text-white">
              intelliExtract
            </span>
            <span className="mt-0.5 text-[10px] uppercase leading-none tracking-widest text-sidebar-foreground/45">
              Studio
            </span>
          </div>
        )}
        {!collapsed && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={toggle}
            className="ml-auto h-6 w-6 shrink-0 text-sidebar-foreground/40 hover:bg-sidebar-accent hover:text-sidebar-foreground"
            aria-label="Collapse sidebar"
          >
            <PanelLeftClose className="h-4 w-4" />
          </Button>
        )}
      </div>

      {collapsed && (
        <div className="flex justify-center border-b border-sidebar-border py-2">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={toggle}
            className="h-7 w-7 text-sidebar-foreground/40 hover:bg-sidebar-accent hover:text-sidebar-foreground"
            aria-label="Expand sidebar"
          >
            <PanelLeftOpen className="h-4 w-4" />
          </Button>
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto px-2 py-4">
        {navGroups.map((group) => (
          <div key={group.group} className="mb-5">
            {!collapsed ? (
              <span className="mb-1.5 block px-2 text-[10px] font-semibold uppercase tracking-widest text-sidebar-foreground/40">
                {group.group}
              </span>
            ) : (
              <div className="mb-1.5 h-px bg-sidebar-border" />
            )}
            <div className="space-y-0.5">
              {group.items.map((feature) => {
                const Icon = FEATURE_ICONS[feature.key];
                const active = isActive(feature.routePath);
                return (
                  <div key={feature.key}>
                    <NavLink
                      to={feature.routePath}
                      title={collapsed ? feature.label : undefined}
                      className={cn(
                        "flex items-center rounded-md py-2 text-sm transition-colors",
                        collapsed ? "justify-center px-0" : "gap-2.5 px-2",
                        active
                          ? "bg-sidebar-primary/20 font-medium text-sidebar-primary"
                          : "text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
                      )}
                    >
                      {!collapsed && (
                        <span
                          className={cn(
                            "h-1.5 w-1.5 shrink-0 rounded-full transition-opacity",
                            active ? "bg-sidebar-primary opacity-100" : "opacity-0",
                          )}
                        />
                      )}
                      <span className={navIconWrapClass(collapsed, active)}>
                        <Icon className="h-4 w-4" />
                      </span>
                      {!collapsed && (
                        <>
                          <span className="flex-1 truncate">{feature.label}</span>
                          {active && (
                            <ChevronRight className="h-3 w-3 shrink-0 text-sidebar-primary/60" />
                          )}
                        </>
                      )}
                    </NavLink>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="shrink-0 border-t border-sidebar-border px-2 py-3">
        <div className={cn("mb-2", collapsed ? "flex justify-center" : "")}>
          <ThemeToggle compact={collapsed} side="right" align={collapsed ? "center" : "start"} />
        </div>
        {collapsed ? (
          <div className="sidebar-user-row sidebar-user-row--collapsed group relative flex justify-center rounded-md py-1">
            <UserAvatar
              userPicture={userPicture}
              pictureFailed={pictureFailed}
              onPictureError={() => setPictureFailed(true)}
              userName={userName}
              userEmail={userEmail}
              size="lg"
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={logout}
              className="sidebar-user-signout absolute inset-0 mx-auto h-8 w-8 text-sidebar-foreground/50 hover:bg-sidebar-accent hover:text-sidebar-foreground"
              aria-label="Sign out"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <div className="sidebar-user-row group relative flex items-center gap-2 rounded-md px-2 py-2 transition-colors hover:bg-sidebar-accent">
            <UserAvatar
              userPicture={userPicture}
              pictureFailed={pictureFailed}
              onPictureError={() => setPictureFailed(true)}
              userName={userName}
              userEmail={userEmail}
            />
            <div className="min-w-0 flex-1 leading-tight">
              {userName && (
                <span className="block truncate text-xs font-medium text-sidebar-accent-foreground">
                  {userName}
                </span>
              )}
              {userEmail && (
                <span
                  className="block truncate text-[10px] text-sidebar-foreground/50"
                  title={userEmail}
                >
                  {userEmail}
                </span>
              )}
              {userRoleLabel && (
                <span className="sidebar-user-role mt-0.5 block truncate text-[10px] text-sidebar-foreground/50">
                  {userRoleLabel}
                </span>
              )}
              {!userName && !userEmail && (
                <span className="block truncate text-xs text-sidebar-foreground/70">User</span>
              )}
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={logout}
              className="sidebar-user-signout absolute right-1 top-1/2 h-8 w-8 -translate-y-1/2 text-sidebar-foreground/50 hover:bg-sidebar-accent hover:text-sidebar-foreground"
              aria-label="Sign out"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Sidebar;
